import { DimensionPanel } from '@carboncopyco/components';
import type { DeviceType, Dimension } from '@carboncopyco/types';
import { type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useScaleDownToWindow } from '../../hooks';
import { useDimensionsContext } from '../../pages/TourCapture/DimensionsContext';
import { customDraggingBoundry } from '../../utils/draggingUtils';

type DimensionPanelWrapperProps = {
  onSubmit: (dimension: Dimension & { type: DeviceType }) => void;
  onDimensionChange: (dimension: Dimension & { type: DeviceType }) => void;
  onClose: () => void;
  activeTabId: number;
  containerRef: RefObject<HTMLElement>;
  anchorRef: RefObject<HTMLElement>;
  visible: boolean;
  offset: [number, number];
  iframeEl: HTMLIFrameElement;
};

const DimensionPanelWrapper = ({
  onSubmit,
  onDimensionChange,
  onClose,
  containerRef,
  anchorRef,
  offset,
  visible,
  iframeEl,
  activeTabId,
}: DimensionPanelWrapperProps) => {
  const {
    setWidth,
    setHeight,
    setType,
    appliedHeight,
    appliedWidth,
    type,
    appliedType,
    setAppliedType,
    setAppliedHeight,
    setAppliedWidth,
    setIsSelected,
    width,
    height,
  } = useDimensionsContext();
  const iframeParentRef = useRef(anchorRef?.current);

  useEffect(() => {
    iframeParentRef?.current?.setAttribute('id', 'ds-dragging-boundry');
    customDraggingBoundry();
  }, []);

  const query = useQuery();

  const dimension = useMemo(() => {
    return {
      width: query.get('width'),
      height: query.get('height'),
      type: query.get('type') as DeviceType,
    };
  }, [query]);

  useEffect(() => {
    if (dimension?.width) {
      const { width: widthStr, height: heightStr } = dimension;
      const width = Number.parseInt(widthStr);
      const height = Number.parseInt(heightStr);
      if (!Number.isNaN(width) && !Number.isNaN(height)) {
        setAppliedWidth(width);
        setAppliedHeight(height);
        setAppliedType(dimension.type);
      }
    }
  }, [dimension, setAppliedWidth, setAppliedHeight, setAppliedType]);

  useEffect(() => {
    setWidth(appliedWidth);
    setHeight(appliedHeight);
    setType(appliedType);
    onDimensionChange({
      width: appliedWidth,
      height: appliedHeight,
      type: appliedType,
    });
  }, [
    appliedWidth,
    appliedHeight,
    appliedType,
    onDimensionChange,
    setWidth,
    setHeight,
    setType,
  ]);

  const handleDesktopResize = useCallback(() => {
    onDimensionChange({
      width,
      height,
      type,
    });
  }, [width, height, type, onDimensionChange]);

  useScaleDownToWindow({
    aspectRatio: type === 'responsive' ? 'responsive' : 'fixedWidthAndHeight',
    targetElement: anchorRef?.current,
    playerIframe: iframeEl,
    initialSize: {
      width,
      height,
    },
    containerRef: containerRef,
    isAnyFrameScreenshot: false,
    replayerWrapper: null,
    handleDesktopResize: handleDesktopResize,
    isCaptureMode: true,
  });

  const handleSubmit = (dimension: Dimension & { type: DeviceType }) => {
    setAppliedHeight(dimension.height);
    setAppliedWidth(dimension.width);
    setAppliedType(dimension.type);
    setIsSelected(true);
    onSubmit(dimension);
  };

  const handleClose = useCallback(() => {
    setWidth(appliedWidth);
    setHeight(appliedHeight);
    setType(appliedType);
    onDimensionChange({
      width: appliedWidth,
      height: appliedHeight,
      type: appliedType,
    });
    onClose();
  }, [
    appliedHeight,
    appliedType,
    appliedWidth,
    onClose,
    onDimensionChange,
    setHeight,
    setType,
    setWidth,
  ]);

  useEffect(() => {
    handleClose();
  }, [activeTabId, handleClose]);

  const handleDimensionChange = (
    dimension: Dimension & { type: DeviceType }
  ) => {
    setWidth(Math.round(dimension.width));
    setHeight(Math.round(dimension.height));
    setType(dimension.type);
    onDimensionChange(dimension);
  };

  if (!visible) {
    return null;
  }

  return (
    <DimensionPanel
      width={appliedWidth}
      height={appliedHeight}
      containerRef={containerRef}
      onSubmit={handleSubmit}
      onClose={handleClose}
      onDimensionChange={handleDimensionChange}
      offset={offset}
      visible={true}
      initialType={appliedType || 'desktop'}
      title="Screen size"
    />
  );
};

export default DimensionPanelWrapper;
